import { useQuery } from "@tanstack/react-query";
import { skillKeys } from "@/lib/query";
import type { Category, ExampleBlock, RiskLevel } from "@/lib/skills/types";

export interface SkillReview {
  id: string;
  rating: number;
  text: string;
  userId: string;
  userName: string;
  userTeam: string | null;
  createdAt: string;
}

export interface SkillVersion {
  id: string;
  v: string;
  notes: string;
  createdAt: string;
}

export interface SkillFinding {
  id: string;
  sev: "high" | "medium" | "low";
  cat: string;
  method: string;
  text: string;
  status: "open" | "acknowledged" | "dismissed";
  createdAt: string;
}

export interface SkillDetail {
  id: string;
  slug: string;
  title: string;
  desc: string;
  whatIs: string;
  whyImportant: string;
  notable: string;
  install: string;
  notes: string;
  version: string;
  risk: RiskLevel;
  published: boolean;
  downloads: number;
  views: number;
  rating: number;
  ratingCount: number;
  github: string;
  cat: Category[];
  useCases: string[];
  examples: ExampleBlock[];
  installSteps: string[];
  authorId: string;
  authorName: string;
  authorTeam: string | null;
  ownerId: string;
  createdAt: string;
  updatedAt: string;
  reviews: SkillReview[];
  history: SkillVersion[];
  findings: SkillFinding[];
}

export function useSkill(slug: string, opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: skillKeys.detail(slug),
    queryFn: async (): Promise<SkillDetail> => {
      const res = await fetch(`/api/skills/${slug}`);
      if (!res.ok) throw new Error("Failed to fetch skill");
      const json = await res.json();
      return json.data;
    },
    enabled: slug.length > 0 && (opts?.enabled ?? true),
  });
}
